import styled from '@emotion/styled';
import { Link } from 'react-router-dom';

import { DISABLED_EXCLUSIONS, focusRing, hoverable } from '../../interaction-states';
import { forHover } from '../../responsive';

export const SIDEBAR_WIDTH_EXPANDED = 194;
export const SIDEBAR_WIDTH_COLLAPSED = 56;

const LOGO_SECTION_HEIGHT = 56;
const MENU_ITEM_HEIGHT = 36;
const LABEL_MAX_WIDTH = SIDEBAR_WIDTH_EXPANDED - 16 - 20 - 16 - 8;
const LOGO_HEIGHT = 32;
const LOGO_SCALE = LOGO_HEIGHT / 36;
const LOGO_FULL_WIDTH = 142 * LOGO_SCALE;
const LOGO_MARK_WIDTH = 32 * LOGO_SCALE;

const MOTION = '.3s cubic-bezier(.05, 0, 0, 1)';

export const StyledSidebar = styled('aside', {
  shouldForwardProp: (prop) => prop !== '$collapsed',
})<{ $collapsed: boolean }>(({ theme, $collapsed }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  backgroundColor: theme.colors.background.default,
  borderRight: `1px solid ${theme.colors.border.subtle}`,

  position: 'sticky',
  top: 0,
  height: '100vh',
  width: $collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED,
  flexShrink: 0,
  zIndex: 1,
  overflowX: 'hidden',
  overflowY: 'auto',
  transition: `width ${MOTION}`,

  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },

  '&::-webkit-scrollbar': {
    display: 'none',
  },
}));

export const TopSection = styled('div')({
  display: 'flex',
  flexDirection: 'column',
});

export const LogoSection = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  height: LOGO_SECTION_HEIGHT,
  flexShrink: 0,
  padding: theme.spacing[12],
  borderBottom: `1px solid ${theme.colors.border.subtle}`,
}));

export const LogoLink = styled(Link)(({ theme }) => ({
  ...hoverable(theme, 'lift'),
  ...focusRing(theme),
  display: 'flex',
  color: theme.colors.white[100],
}));

export const LogoSwap = styled('span', {
  shouldForwardProp: (prop) => prop !== '$collapsed',
})<{ $collapsed: boolean }>(({ $collapsed }) => ({
  display: 'block',
  flexShrink: 0,
  height: LOGO_HEIGHT,
  width: $collapsed ? LOGO_MARK_WIDTH : LOGO_FULL_WIDTH,
  overflow: 'hidden',
  transition: `width ${MOTION}`,

  svg: {
    display: 'block',
    width: LOGO_FULL_WIDTH,
    height: LOGO_HEIGHT,
  },

  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}));

export const MenuSection = styled('nav')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing[8],
}));

export const BottomSection = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[16],
  padding: theme.spacing[8],
}));

export const SidebarMenu = styled('ul')({
  display: 'flex',
  flexDirection: 'column',
  margin: 0,
  padding: 0,
  listStyle: 'none',
});

export const SidebarSubMenu = styled(SidebarMenu)(({ theme }) => ({
  paddingLeft: theme.spacing[16],
}));

export const ItemLabel = styled('span', {
  shouldForwardProp: (prop) => prop !== '$collapsed',
})<{ $collapsed: boolean }>(({ theme, $collapsed }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: $collapsed ? 0 : theme.spacing[8],
  minWidth: 0,
  transition: `gap ${MOTION}`,

  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}));

export const ItemText = styled('span', {
  shouldForwardProp: (prop) => prop !== '$collapsed',
})<{ $collapsed: boolean }>(({ $collapsed }) => ({
  display: 'block',
  minWidth: 0,
  overflow: 'hidden',
  opacity: $collapsed ? 0 : 1,
  maxWidth: $collapsed ? 0 : LABEL_MAX_WIDTH,
  transition: `opacity ${MOTION}, max-width ${MOTION}`,
  willChange: 'opacity, max-width',

  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}));

export const IndicatorDot = styled('span')({
  position: 'absolute',
  top: 'calc(50% - 7px)',
  left: 'calc(50% + 7px)',
  transform: 'translate(-50%, -50%)',
  pointerEvents: 'none',
});

export const SidebarMenuItem = styled('li', {
  shouldForwardProp: (prop) => !['active', 'disabled', '$collapsed'].includes(prop),
})<{ active: boolean; disabled: boolean; $collapsed: boolean }>(
  ({ theme, active, disabled, $collapsed }) => ({
    display: 'flex',
    opacity: disabled ? 0.5 : 1,

    'a, div[aria-disabled]': {
      flexGrow: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      position: 'relative',
      isolation: 'isolate',
      height: MENU_ITEM_HEIGHT,
      boxSizing: 'border-box',
      gap: theme.spacing[8],
      padding: $collapsed
        ? `${theme.spacing[8]}px ${theme.spacing[12]}px`
        : `${theme.spacing[8]}px ${theme.spacing[8]}px ${theme.spacing[8]}px ${theme.spacing[12]}px`,
      borderRadius: theme.radius[8],
      whiteSpace: 'nowrap',
      textDecoration: 'none',
      backgroundColor: active ? theme.colors.brand.subtle : 'transparent',
      color: active ? theme.colors.text.brand : theme.colors.text.secondary,
      transition: `color ${MOTION}, background-color ${MOTION}`,
    },

    'div[aria-disabled]': {
      cursor: 'default',
    },

    svg: {
      width: 16,
      height: 16,
      flexShrink: 0,
      color: active ? theme.colors.icon.brand : theme.colors.icon.secondary,
      transition: `color ${MOTION}`,
    },

    ...(!active && {
      'a::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        zIndex: -1,
        borderRadius: 'inherit',
        backgroundColor: theme.colors.neutral.solid,
        opacity: 0,
        transform: 'scale(0.9)',
        transition: 'all .3s cubic-bezier(.05, 0, 0, 1)',
        transitionProperty: 'opacity, transform',
        willChange: 'opacity, transform',
      },

      [forHover]: {
        [`a:hover${DISABLED_EXCLUSIONS}`]: {
          color: theme.colors.text.primary,
        },
        [`a:hover${DISABLED_EXCLUSIONS} svg`]: {
          color: theme.colors.icon.primary,
        },
        [`a:hover${DISABLED_EXCLUSIONS}::before`]: {
          opacity: 1,
          transform: 'scale(1)',
        },
      },

      [`a:active${DISABLED_EXCLUSIONS}`]: {
        color: theme.colors.text.primary,
      },
      [`a:active${DISABLED_EXCLUSIONS} svg`]: {
        color: theme.colors.icon.primary,
      },
      [`a:active${DISABLED_EXCLUSIONS}::before`]: {
        opacity: 1,
        transform: 'scale(1)',
      },

      '@media (prefers-reduced-motion: reduce)': {
        'a::before': {
          transition: 'opacity 0.01ms',
          transform: 'none',
        },
        [`a:hover${DISABLED_EXCLUSIONS}::before`]: {
          transform: 'none',
        },
      },
    }),

    ...focusRing(theme, { target: 'a' }),
  }),
);
